import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Collapse,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { Grid } from '@mui/material';
import { colors, defaultInputSX } from '@src/styles';
import type { AliareFieldMapping, OrgSetupCustomField } from '@src/services/org-config/dto';
import type { LutienOrganizationsGetResponseDTO } from '@src/services/lutien/modules/v1/organizations/dto/lutien-organizations.get.response.dto';

const ALIARE_TO_TARKEN_MAPPING: Record<string, string> = {
  cpfCnpj: 'govRegCode',
  email: 'email',
  emailVendedor: 'assignedUserId',
  endereco: 'Custom Field Address',
  nomeFantasia: 'name',
  razaoSocial: 'name',
  telefone: 'phoneNumber',
};

export type AliareFieldMappingViewProps = {
  organizations: LutienOrganizationsGetResponseDTO;
  isLoadingOrgs: boolean;
  organizationId: string;
  mappings: AliareFieldMapping[];
  customerCustomFields: OrgSetupCustomField[];
  isLoading: boolean;
  isSaving: boolean;
  saveSuccessOpen?: boolean;
  onCloseSaveSuccess?: () => void;
  saveErrorOpen?: boolean;
  saveErrorMessage?: string;
  onCloseSaveError?: () => void;
  onMappingChange: (
    aliareField: string,
    field: 'customerUnitField' | 'customerGroupField' | 'transformer',
    value: string,
  ) => void;
  onSave: () => void;
  onExportExcel: () => void;
  onImportExcel: (file: File) => void;
  onBackToOrgConfig: () => void;
};

interface CustomFieldSelectProps {
  value: string;
  onChange: (value: string) => void;
  customerFields: OrgSetupCustomField[];
  disabled?: boolean;
}

const CustomFieldSelect: React.FC<CustomFieldSelectProps> = ({ value, onChange, customerFields, disabled = false }) => {
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [inputValue, setInputValue] = useState(value);

  React.useEffect(() => {
    const fieldExists = customerFields.some((field) => field.identifier === value);
    setIsCustomMode(!fieldExists && value !== '');
    setInputValue(value);
  }, [value, customerFields]);

  const handleSelectChange = (selectedValue: string) => {
    if (selectedValue === '__custom__') {
      setIsCustomMode(true);
      setInputValue('');
    } else {
      setIsCustomMode(false);
      setInputValue(selectedValue);
      onChange(selectedValue);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    onChange(newValue);
  };

  const handleInputBlur = () => {
    if (!inputValue.trim()) {
      setIsCustomMode(false);
    }
  };

  if (isCustomMode) {
    return (
      <TextField
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        size="small"
        fullWidth
        placeholder="Digite o identificador do campo..."
        disabled={disabled}
        sx={{
          fontSize: '0.875rem',
          '& .MuiOutlinedInput-root': {
            backgroundColor: `${colors.background.primary}80`,
            borderRadius: 2,
            '& fieldset': {
              borderColor: `${colors.primary.yellow}40`,
            },
            '&:hover fieldset': {
              borderColor: colors.primary.yellow,
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.primary.yellow,
              borderWidth: 2,
            },
          },
          '& .MuiInputBase-input': {
            color: colors.primary.white,
            fontSize: '0.875rem',
          },
          '& .MuiInputBase-input::placeholder': {
            color: colors.primary.white,
            opacity: 0.7,
          },
        }}
        InputProps={{
          endAdornment: (
            <IconButton
              size="small"
              onClick={() => {
                setIsCustomMode(false);
                setInputValue('');
                onChange('');
              }}
              disabled={disabled}
              sx={{
                color: colors.primary.yellow,
                opacity: 0.7,
                '&:hover': {
                  opacity: 1,
                  backgroundColor: `${colors.primary.yellow}15`,
                },
              }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          ),
        }}
      />
    );
  }

  return (
    <FormControl fullWidth size="small">
      <Select
        value={inputValue}
        onChange={(e) => handleSelectChange(e.target.value)}
        disabled={disabled}
        displayEmpty
        sx={{
          fontSize: '0.875rem',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: `${colors.primary.yellow}40`,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary.yellow,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary.yellow,
            borderWidth: 2,
          },
          '& .MuiSelect-select': {
            color: colors.primary.white,
            backgroundColor: `${colors.background.primary}80`,
            borderRadius: 2,
          },
          '& .MuiSelect-icon': {
            color: colors.primary.yellow,
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              backgroundColor: colors.background.primary,
              border: `1px solid ${colors.primary.yellow}40`,
              '& .MuiMenuItem-root': {
                color: colors.primary.white,
                fontSize: '0.875rem',
                '&:hover': {
                  backgroundColor: `${colors.primary.yellow}15`,
                },
                '&.Mui-selected': {
                  backgroundColor: `${colors.primary.yellow}25`,
                  '&:hover': {
                    backgroundColor: `${colors.primary.yellow}35`,
                  },
                },
              },
            },
          },
        }}
      >
        <MenuItem value="">
          <em style={{ color: colors.primary.white, opacity: 0.7 }}>Selecione um campo...</em>
        </MenuItem>
        {customerFields.map((field) => (
          <MenuItem key={field.id} value={field.identifier}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography variant="body2" fontWeight="500">
                {field.label}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.primary.yellow, opacity: 0.8 }}>
                ({field.identifier})
              </Typography>
            </Box>
          </MenuItem>
        ))}
        <MenuItem value="__custom__">
          <Box sx={{ display: 'flex', alignItems: 'center', color: colors.primary.blue }}>
            <AddIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="body2" fontWeight="500">
              Adicionar campo personalizado...
            </Typography>
          </Box>
        </MenuItem>
      </Select>
    </FormControl>
  );
};

export const AliareFieldMappingView = ({
  organizations,
  organizationId,
  mappings,
  customerCustomFields,
  isLoading,
  isSaving,
  saveSuccessOpen,
  onCloseSaveSuccess,
  saveErrorOpen,
  saveErrorMessage,
  onCloseSaveError,
  onMappingChange,
  onSave,
  onExportExcel,
  onImportExcel,
  onBackToOrgConfig,
}: AliareFieldMappingViewProps) => {
  const [fileInputKey, setFileInputKey] = useState<number>(0);
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [expandedTransformers, setExpandedTransformers] = useState<Set<string>>(new Set());

  const filteredAndSortedMappings = useMemo(() => {
    let filtered = mappings;

    if (searchFilter.trim()) {
      const search = searchFilter.toLowerCase();
      filtered = mappings.filter(
        (mapping) =>
          mapping.aliareField.toLowerCase().includes(search) ||
          (mapping.customerUnitField || '').toLowerCase().includes(search) ||
          (mapping.customerGroupField || '').toLowerCase().includes(search) ||
          (mapping.description || '').toLowerCase().includes(search),
      );
    }

    return filtered.sort((a, b) => {
      if (a.isFixedField && !b.isFixedField) return -1;
      if (!a.isFixedField && b.isFixedField) return 1;
      return a.aliareField.localeCompare(b.aliareField);
    });
  }, [mappings, searchFilter]);

  const clearFilters = () => {
    setSearchFilter('');
  };

  const toggleTransformer = (aliareField: string) => {
    setExpandedTransformers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(aliareField)) {
        newSet.delete(aliareField);
      } else {
        newSet.add(aliareField);
      }
      return newSet;
    });
  };

  const hasTransformer = (mapping: AliareFieldMapping) => {
    return (mapping.transformer || '').trim() !== '';
  };

  const localInputSX = {
    ...defaultInputSX,
    marginBottom: 0,
    '& .MuiInputBase-input::placeholder': {
      color: 'rgba(255,255,255,0.7)',
      opacity: 1,
    },
    '& .MuiOutlinedInput-root': {
      ...defaultInputSX['& .MuiOutlinedInput-root'],
      minHeight: '36px',
    },
  } as const;

  const selectedOrg = organizations.find((org) => org.id === organizationId);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onImportExcel(file);
      setFileInputKey((prev) => prev + 1);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.background.primary,
        height: '100%',
      }}
    >
      <Box sx={{ flex: 1, p: { md: 4 }, overflow: 'auto' }}>
        <Box
          sx={{
            mb: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${colors.background.secondary}15 0%, ${colors.primary.blue}10 100%)`,
            border: `1px solid ${colors.background.secondary}40`,
          }}
        >
          <IconButton
            onClick={onBackToOrgConfig}
            sx={{
              color: colors.primary.yellow,
              backgroundColor: `${colors.primary.yellow}15`,
              border: `1px solid ${colors.primary.yellow}30`,
              '&:hover': {
                backgroundColor: colors.primary.yellow,
                color: colors.background.primary,
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 12px ${colors.primary.yellow}30`,
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography
              variant="h4"
              sx={{
                color: colors.primary.white,
                fontWeight: 600,
                mb: 0.5,
                fontSize: { xs: '1.5rem', md: '2rem' },
              }}
            >
              Mapeamento de Campos - Aliare
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: colors.primary.yellow,
                opacity: 0.9,
              }}
            >
              Configure os mapeamentos entre campos Aliare e Customer
            </Typography>
          </Box>
        </Box>

        {selectedOrg && (
          <Box sx={{ mb: 3 }}>
            <Chip
              label={`Organização: ${selectedOrg.fantasyName || selectedOrg.corporateName || 'N/A'}`}
              sx={{
                backgroundColor: colors.primary.blue,
                color: colors.primary.white,
                fontWeight: 500,
                fontSize: '0.875rem',
                height: 36,
                '&:hover': {
                  backgroundColor: colors.primary.blue,
                  opacity: 0.9,
                },
              }}
              size="medium"
            />
          </Box>
        )}

        <Alert
          severity="warning"
          sx={{
            mb: 3,
            backgroundColor: `${colors.primary.yellow}10`,
            borderColor: colors.primary.yellow,
            border: `1px solid ${colors.primary.yellow}40`,
            borderRadius: 2,
            '& .MuiAlert-icon': {
              color: colors.primary.yellow,
            },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: colors.primary.white,
            }}
          >
            <strong style={{ color: colors.primary.yellow }}>Atenção:</strong> O salvamento irá substituir completamente
            todo o mapeamento existente para esta organização. Apenas os campos preenchidos serão mantidos na
            configuração.
          </Typography>
        </Alert>

        <Paper
          elevation={0}
          sx={{
            mb: 3,
            backgroundColor: colors.background.secondary,
            border: `1px solid ${colors.background.secondary}60`,
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              p: 3,
              borderBottom: `1px solid ${colors.background.secondary}80`,
              background: `linear-gradient(135deg, ${colors.background.secondary}20 0%, ${colors.primary.blue}05 100%)`,
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  size="small"
                  placeholder="Buscar por Campo Aliare ou Identifier..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  fullWidth
                  sx={{
                    ...localInputSX,
                    '& .MuiOutlinedInput-root': {
                      ...localInputSX['& .MuiOutlinedInput-root'],
                      backgroundColor: `${colors.background.primary}90`,
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: colors.primary.yellow,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: colors.primary.yellow,
                        borderWidth: 2,
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: '18px', color: colors.primary.yellow }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchFilter && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={clearFilters}
                          sx={{
                            padding: '4px',
                            color: colors.primary.yellow,
                            '&:hover': {
                              backgroundColor: `${colors.primary.yellow}20`,
                            },
                          }}
                          title="Limpar filtros"
                        >
                          <ClearIcon sx={{ fontSize: '16px' }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={onExportExcel}
                  disabled={isLoading}
                  sx={{
                    borderColor: colors.primary.blue,
                    color: colors.primary.blue,
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: colors.primary.blue,
                      color: colors.primary.white,
                      borderColor: colors.primary.blue,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 4px 12px ${colors.primary.blue}30`,
                    },
                    '&:disabled': {
                      borderColor: colors.background.secondary,
                      color: colors.background.secondary,
                    },
                    borderRadius: 2,
                    fontWeight: 500,
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  Exportar
                </Button>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<FileUploadIcon />}
                  disabled={isLoading}
                  sx={{
                    borderColor: colors.primary.green,
                    color: colors.primary.green,
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: colors.primary.green,
                      color: colors.primary.white,
                      borderColor: colors.primary.green,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 4px 12px ${colors.primary.green}30`,
                    },
                    '&:disabled': {
                      borderColor: colors.background.secondary,
                      color: colors.background.secondary,
                    },
                    borderRadius: 2,
                    fontWeight: 500,
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  Importar
                  <input key={fileInputKey} type="file" hidden accept=".csv,.xlsx,.xls" onChange={handleFileUpload} />
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={onSave}
                  disabled={isLoading || isSaving}
                  sx={{
                    backgroundColor: colors.primary.yellow,
                    color: colors.background.primary,
                    border: 'none',
                    '&:hover': {
                      backgroundColor: colors.primary.yellow,
                      opacity: 0.9,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 4px 12px ${colors.primary.yellow}40`,
                    },
                    '&:disabled': {
                      backgroundColor: colors.background.secondary,
                      color: colors.primary.gray,
                    },
                    borderRadius: 2,
                    fontWeight: 600,
                    transition: 'all 0.2s ease-in-out',
                    minWidth: 120,
                  }}
                >
                  {isSaving ? 'Salvando...' : 'Salvar'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            backgroundColor: colors.background.secondary,
            border: `1px solid ${colors.background.secondary}60`,
            borderRadius: 3,
            width: 'calc(100% - 2px)',
          }}
        >
          <Table
            size="small"
            sx={{
              '& .MuiTableCell-root': {
                padding: '12px 16px',
                borderBottom: `1px solid ${colors.background.secondary}40`,
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: `${colors.background.primary}95`,
                    borderBottom: `2px solid ${colors.primary.yellow}`,
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight="700"
                    sx={{
                      color: colors.primary.yellow,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontSize: '0.75rem',
                    }}
                  >
                    Campo Aliare
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: `${colors.background.primary}95`,
                    borderBottom: `2px solid ${colors.primary.yellow}`,
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight="700"
                    sx={{
                      color: colors.primary.yellow,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontSize: '0.75rem',
                    }}
                  >
                    Campo Customer Unit
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: `${colors.background.primary}95`,
                    borderBottom: `2px solid ${colors.primary.yellow}`,
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight="700"
                    sx={{
                      color: colors.primary.yellow,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontSize: '0.75rem',
                    }}
                  >
                    Campo Customer Group
                  </Typography>
                </TableCell>
                <TableCell
                  width={60}
                  sx={{
                    backgroundColor: `${colors.background.primary}95`,
                    borderBottom: `2px solid ${colors.primary.yellow}`,
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight="700"
                    sx={{
                      color: colors.primary.yellow,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontSize: '0.75rem',
                    }}
                  >
                    Transformer
                  </Typography>
                </TableCell>
                <TableCell
                  width={40}
                  sx={{
                    backgroundColor: `${colors.background.primary}95`,
                    borderBottom: `2px solid ${colors.primary.yellow}`,
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight="700"
                    sx={{
                      color: colors.primary.yellow,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontSize: '0.75rem',
                    }}
                  >
                    Info
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAndSortedMappings.map((mapping, index) => (
                <React.Fragment key={mapping.aliareField}>
                  <TableRow
                    sx={{
                      backgroundColor: index % 2 === 0 ? `${colors.background.primary}40` : 'transparent',
                      '&:hover': {
                        backgroundColor: `${colors.primary.yellow}08`,
                      },
                      transition: 'background-color 0.2s ease-in-out',
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography
                          variant="body2"
                          fontWeight="600"
                          sx={{
                            color: colors.primary.white,
                            fontSize: '0.875rem',
                          }}
                        >
                          {mapping.aliareField}
                        </Typography>
                        {mapping.isFixedField && (
                          <Chip
                            label="Nativo"
                            size="small"
                            sx={{
                              backgroundColor: colors.primary.blue,
                              color: colors.primary.white,
                              fontSize: '0.7rem',
                              height: '20px',
                              fontWeight: 500,
                            }}
                          />
                        )}
                      </Box>
                      {mapping.description && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.primary.white,
                            opacity: 0.7,
                            fontSize: '0.75rem',
                          }}
                        >
                          {mapping.description}
                        </Typography>
                      )}
                    </TableCell>

                    {mapping.isFixedField ? (
                      <>
                        <TableCell colSpan={2} sx={{ padding: '8px 12px' }}>
                          <Box
                            sx={{
                              backgroundColor: `${colors.background.primary}80`,
                              border: `1px solid ${colors.primary.yellow}40`,
                              borderRadius: 2,
                              padding: '12px 16px',
                              minHeight: '40px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '100%',
                              boxSizing: 'border-box',
                              background: `linear-gradient(135deg, ${colors.background.primary}60 0%, ${colors.primary.blue}10 100%)`,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: colors.primary.white,
                                fontWeight: 500,
                              }}
                            >
                              <Box component="span" sx={{ fontWeight: 'bold', color: colors.primary.yellow }}>
                                Tarken Field:
                              </Box>{' '}
                              {ALIARE_TO_TARKEN_MAPPING[mapping.aliareField] || mapping.fixedValue || '-'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: colors.primary.white,
                              opacity: 0.5,
                              fontStyle: 'italic',
                            }}
                          >
                            -
                          </Typography>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>
                          <CustomFieldSelect
                            value={mapping.customerUnitField || ''}
                            onChange={(value) => onMappingChange(mapping.aliareField, 'customerUnitField', value)}
                            customerFields={customerCustomFields}
                            disabled={isLoading}
                          />
                        </TableCell>
                        <TableCell>
                          <CustomFieldSelect
                            value={mapping.customerGroupField || ''}
                            onChange={(value) => onMappingChange(mapping.aliareField, 'customerGroupField', value)}
                            customerFields={customerCustomFields}
                            disabled={isLoading}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={hasTransformer(mapping) || expandedTransformers.has(mapping.aliareField)}
                                  onChange={() => toggleTransformer(mapping.aliareField)}
                                  disabled={isLoading}
                                  sx={{
                                    color: colors.primary.yellow,
                                    '&.Mui-checked': {
                                      color: colors.primary.yellow,
                                    },
                                  }}
                                />
                              }
                              label=""
                              sx={{ margin: 0 }}
                            />
                            {(hasTransformer(mapping) || expandedTransformers.has(mapping.aliareField)) && (
                              <IconButton
                                size="small"
                                onClick={() => toggleTransformer(mapping.aliareField)}
                                title="Configurar transformer"
                                sx={{
                                  color: colors.primary.blue,
                                  opacity: 0.7,
                                  transition: 'all 0.2s ease-in-out',
                                  '&:hover': {
                                    opacity: 1,
                                    backgroundColor: `${colors.primary.blue}15`,
                                    transform: 'scale(1.1)',
                                  },
                                }}
                              >
                                <AddIcon sx={{ fontSize: '16px' }} />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </>
                    )}

                    <TableCell>
                      {mapping.description && (
                        <Tooltip title={mapping.description}>
                          <IconButton
                            size="small"
                            sx={{
                              padding: '4px',
                              color: colors.primary.yellow,
                              opacity: 0.6,
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                opacity: 1,
                                backgroundColor: `${colors.primary.yellow}15`,
                                transform: 'scale(1.1)',
                              },
                            }}
                          >
                            <InfoIcon sx={{ fontSize: '16px' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>

                  {!mapping.isFixedField &&
                    (hasTransformer(mapping) || expandedTransformers.has(mapping.aliareField)) && (
                      <TableRow key={`${mapping.aliareField}-transformer`}>
                        <TableCell
                          colSpan={5}
                          sx={{
                            padding: '12px 16px',
                            backgroundColor: `${colors.background.primary}60`,
                            borderTop: `1px solid ${colors.primary.yellow}40`,
                          }}
                        >
                          <Collapse in={hasTransformer(mapping) || expandedTransformers.has(mapping.aliareField)}>
                            <Box sx={{ p: 2 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  mb: 2,
                                  fontWeight: 600,
                                  color: colors.primary.yellow,
                                  fontSize: '0.875rem',
                                }}
                              >
                                Transformer JSON:
                              </Typography>
                              <TextField
                                value={mapping.transformer || ''}
                                onChange={(e) => onMappingChange(mapping.aliareField, 'transformer', e.target.value)}
                                size="small"
                                placeholder='{"type": "custom", "config": {...}}'
                                fullWidth
                                multiline
                                rows={3}
                                sx={{
                                  ...localInputSX,
                                  '& .MuiOutlinedInput-root': {
                                    ...localInputSX['& .MuiOutlinedInput-root'],
                                    backgroundColor: `${colors.background.primary}80`,
                                    fontFamily: 'monospace',
                                    fontSize: '0.8rem',
                                  },
                                }}
                                disabled={isLoading}
                              />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Backdrop open={isLoading || isSaving} sx={{ zIndex: 1300 }}>
        <CircularProgress color="primary" />
      </Backdrop>

      <Dialog open={saveSuccessOpen || false}>
        <DialogTitle>Sucesso</DialogTitle>
        <DialogContent>
          <Typography>Mapeamentos salvos com sucesso!</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseSaveSuccess} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={saveErrorOpen || false}>
        <DialogTitle>Erro</DialogTitle>
        <DialogContent>
          <Typography>{saveErrorMessage || 'Ocorreu um erro ao salvar os mapeamentos.'}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseSaveError} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
